import { maxAnnualSaving } from '@nolens/domain';
import { catalog } from '@nolens/i18n';

/** Definições de um espaço: a página com os cartões e as páginas Geral, Gravações e Plano. */
export const settingsText = catalog(
  {
    title: 'Settings',
    spaceNotFound: 'Space not found.',
    onlyTeams: 'Only teams have these settings.',
    personalSpace: 'Personal space',
    team: 'Team',
    groupSpace: 'Space',
    groupPeople: 'Members and privacy',
    groupPlan: 'Plan',
    groupAccount: 'Your account',
    general: 'General',
    generalHint: 'Team name, leave or delete the team',
    brand: 'Brand',
    brandHint: 'Colour, logo, font and footer of the exports',
    exportGuides: 'Export guides',
    exportHint: 'All the guides in a ZIP (Markdown and screenshots)',
    exportIntro:
      'A ZIP file with every guide in this space: one folder per guide, with the guide in Markdown and its screenshots, plus an index. Use it to keep a copy or to take the guides to another tool.',
    exportNote: (max: number) =>
      `Up to ${max} guides per export. With many screenshots, the ZIP is split into parts of about 200 MB; the browser may ask to allow several downloads.`,
    exportButton: 'Export (ZIP)',
    exportProgress: (done: number, total: number) => `Preparing… ${done} of ${total} guides`,
    exportDone: (guides: number, parts: number) =>
      `${guides} ${guides === 1 ? 'guide' : 'guides'} exported${parts > 1 ? ` in ${parts} files` : ''}.`,
    exportMissing: (n: number) =>
      `${n} ${n === 1 ? 'screenshot' : 'screenshots'} could not be loaded and ${n === 1 ? 'was' : 'were'} left out.`,
    exportEmpty: 'This space has no guides yet.',
    exportFailed: 'The export failed. Check your connection and try again.',
    exportOnlyOwner: 'Only the team owner can export all its guides.',
    members: 'Members',
    membersHint: 'People, roles and invite links',
    recordings: 'Recordings',
    recordingsHint: 'Review before sending and the text typed in fields',
    plan: 'Plan and usage',
    planHint: 'Plan, guides and screenshot storage',
    account: 'Account',
    accountHint: 'Your email and deleting the account',
    extension: 'Extension',
    extensionHint: 'Install the Chrome extension and connect it',
    // Geral
    teamName: 'Team name',
    save: 'Save',
    saved: 'Name saved.',
    onlyOwnerRenames: 'Only an owner can change the team name.',
    leaveOrDelete: 'Leave or delete',
    leaveHint: 'Leaving, you stop seeing this team’s guides. Deleting removes them for everyone.',
    // Gravações (quem não é dono)
    reviewOptional: 'In this team, reviewing before sending is optional.',
    keepsTyped: 'In this team, the text typed in fields is kept.',
    // Plano
    planTitle: 'Plan and usage',
    planIntro: 'What this space’s plan includes and how much of it is in use.',
    guides: 'Guides',
    unlimited: 'Unlimited',
    screenshots: 'Screenshots',
    storageOf: (mb: string, gb: string) => `${mb} MB of ${gb} GB`,
    seePlans: 'See plans',
    // Subscrição (Paddle)
    subscription: 'Subscription',
    billingInterval: 'Billing',
    monthly: 'Monthly',
    yearly: `Yearly, save up to ${maxAnnualSaving()}%`,
    perMonth: (price: string) => `${price} / month + VAT`,
    perYear: (price: string) => `${price} / year + VAT`,
    subscribePro: 'Subscribe to Pro',
    subscribeTeam: 'Subscribe to Pro Team',
    teamSeats: (seats: number, min: number) =>
      `${seats} editors billed (minimum ${min}). When editors join or leave, the number is updated and prorated.`,
    teamTotal: (seats: number, price: string) => `${seats} × ${price}`,
    onlyOwnerBuys: 'Only an owner of the team can subscribe or manage the subscription.',
    activeUntil: (date: string) => `Subscription active. Next renewal: ${date}.`,
    pastDue: 'The last payment failed. Update the card in “Manage subscription” to keep the plan.',
    canceledUntil: (date: string) => `Subscription cancelled: the plan ends on ${date}.`,
    seatsBilled: (n: number) => `${n} editors billed.`,
    currentPlan: 'Current plan',
    scopePersonal:
      'This is the plan of your personal space. Each team has its own plan (Pro Team), in the team’s settings.',
    scopeTeam: (name: string) => `This is the plan of the team ${name}, for all its members.`,
    stateFree: 'No subscription.',
    stateTrial: (date: string) => `Trial until ${date}.`,
    stateGranted: (date: string | null) =>
      date ? `Active without a subscription, until ${date}.` : 'Active without a subscription.',
    notSubscribedYet: (plan: string, date: string | null) =>
      `Your ${plan} is active without a subscription${date ? ` until ${date}` : ''}. To keep it after that, subscribe here.`,
    manage: 'Manage subscription',
    manageHint: 'Cancel, change the card or download invoices.',
    paid: 'Payment received. The plan will be active in a few seconds.',
    paddleNote:
      'Payments are processed securely by Paddle, our reseller, which adds VAT at checkout.',
    refunds: 'Refund policy',
    checkoutFailed: 'Couldn’t open the payment page. Check your connection and try again.',
    paymentsSoon: 'Online payments aren’t live yet: write to us and we’ll activate the plan.',
    planSubject: (plan: string) => `${plan} plan — Nolens Guides`,
  },
  {
    'pt-PT': {
      title: 'Definições',
      spaceNotFound: 'Espaço não encontrado.',
      onlyTeams: 'Só as equipas têm estas definições.',
      personalSpace: 'Espaço pessoal',
      team: 'Equipa',
      groupSpace: 'Espaço',
      groupPeople: 'Membros e privacidade',
      groupPlan: 'Plano',
      groupAccount: 'A sua conta',
      general: 'Geral',
      generalHint: 'Nome da equipa, sair ou apagar a equipa',
      brand: 'Marca',
      brandHint: 'Cor, logótipo, letra e rodapé das exportações',
      exportGuides: 'Exportar guias',
      exportHint: 'Todos os guias num ZIP (Markdown e capturas)',
      exportIntro:
        'Um ficheiro ZIP com todos os guias deste espaço: uma pasta por guia, com o guia em Markdown e as capturas, e um índice. Serve para guardar uma cópia ou levar os guias para outra ferramenta.',
      exportNote: (max) =>
        `Até ${max} guias por exportação. Com muitas capturas, o ZIP divide-se em partes de cerca de 200 MB; o browser pode pedir para autorizar vários ficheiros.`,
      exportButton: 'Exportar (ZIP)',
      exportProgress: (done, total) => `A preparar… ${done} de ${total} guias`,
      exportDone: (guides, parts) =>
        `${guides} ${guides === 1 ? 'guia exportado' : 'guias exportados'}${parts > 1 ? ` em ${parts} ficheiros` : ''}.`,
      exportMissing: (n) =>
        `${n} ${n === 1 ? 'captura não carregou e ficou' : 'capturas não carregaram e ficaram'} de fora.`,
      exportEmpty: 'Este espaço ainda não tem guias.',
      exportFailed: 'A exportação falhou. Verifique a ligação e tente outra vez.',
      exportOnlyOwner: 'Só o dono da equipa pode exportar todos os guias.',
      members: 'Membros',
      membersHint: 'Pessoas, papéis e ligações de convite',
      recordings: 'Gravações',
      recordingsHint: 'Rever antes de enviar e o texto escrito nos campos',
      plan: 'Plano e utilização',
      planHint: 'Plano, guias e espaço das capturas',
      account: 'Conta',
      accountHint: 'O seu email e apagar a conta',
      extension: 'Extensão',
      extensionHint: 'Instalar a extensão do Chrome e ligá-la',
      teamName: 'Nome da equipa',
      save: 'Guardar',
      saved: 'Nome guardado.',
      onlyOwnerRenames: 'Só um dono pode mudar o nome da equipa.',
      leaveOrDelete: 'Sair ou apagar',
      leaveHint: 'Ao sair, deixa de ver os guias desta equipa. Apagar remove-os para todos.',
      reviewOptional: 'Nesta equipa, rever antes de enviar é opcional.',
      keepsTyped: 'Nesta equipa, o texto escrito nos campos é guardado.',
      planTitle: 'Plano e utilização',
      planIntro: 'O que o plano deste espaço inclui e quanto já está em uso.',
      guides: 'Guias',
      unlimited: 'Ilimitados',
      screenshots: 'Capturas',
      storageOf: (mb, gb) => `${mb} MB de ${gb} GB`,
      seePlans: 'Ver planos',
      subscription: 'Subscrição',
      billingInterval: 'Faturação',
      monthly: 'Mensal',
      yearly: `Anual, poupe até ${maxAnnualSaving()} %`,
      perMonth: (price) => `${price} / mês + IVA`,
      perYear: (price) => `${price} / ano + IVA`,
      subscribePro: 'Subscrever o Pro',
      subscribeTeam: 'Subscrever o Pro Equipa',
      teamSeats: (seats, min) =>
        `${seats} editores faturados (mínimo ${min}). Quando entram ou saem editores, o número é atualizado e cobrado na proporção.`,
      teamTotal: (seats, price) => `${seats} × ${price}`,
      onlyOwnerBuys: 'Só um dono da equipa pode subscrever ou gerir a subscrição.',
      activeUntil: (date) => `Subscrição ativa. Próxima renovação: ${date}.`,
      pastDue:
        'O último pagamento falhou. Atualize o cartão em «Gerir subscrição» para manter o plano.',
      canceledUntil: (date) => `Subscrição cancelada: o plano termina a ${date}.`,
      seatsBilled: (n) => `${n} editores faturados.`,
      currentPlan: 'Plano atual',
      scopePersonal:
        'Este é o plano do seu espaço pessoal. Cada equipa tem o seu próprio plano (Pro Equipa), nas definições da equipa.',
      scopeTeam: (name) => `Este é o plano da equipa ${name}, para todos os membros.`,
      stateFree: 'Sem subscrição.',
      stateTrial: (date) => `Em teste até ${date}.`,
      stateGranted: (date) =>
        date ? `Ativo sem subscrição, até ${date}.` : 'Ativo sem subscrição.',
      notSubscribedYet: (plan, date) =>
        `O seu ${plan} está ativo sem subscrição${date ? ` até ${date}` : ''}. Para o manter depois disso, subscreva aqui.`,
      manage: 'Gerir subscrição',
      manageHint: 'Cancelar, mudar o cartão ou descarregar as faturas.',
      paid: 'Pagamento recebido. O plano fica ativo dentro de segundos.',
      paddleNote:
        'Os pagamentos são processados com segurança pelo Paddle, o nosso revendedor, que soma o IVA no pagamento.',
      refunds: 'Política de reembolso',
      checkoutFailed:
        'Não foi possível abrir a página de pagamento. Verifique a ligação e tente outra vez.',
      paymentsSoon: 'Os pagamentos online ainda não estão ativos: escreva-nos e ativamos o plano.',
      planSubject: (plan) => `Plano ${plan} — Nolens Guides`,
    },
  },
);
